import { toast } from "sonner";
import { jsPDF } from "jspdf";
import api, { imageUrl } from "@/lib/api";
import { storeForCategory, buildItemUrl } from "@/lib/shop";

// Strip markdown from the AI explanation and split into clean paragraph lines (mirrors StepViewer).
function cleanExplain(text) {
  return String(text || "")
    .replace(/\r/g, "")
    .replace(/\*\*/g, "")
    .replace(/[*_`#>]/g, "")
    .split(/\n+/)
    .map((l) => l.replace(/^\s*[-•·]\s*/, "").trim())
    .filter(Boolean);
}

export async function shareManual(manual) {
  const isMinute = manual.mode === "minute";
  const text = isMinute
    ? `Watch my 60-second reel: "${manual.title}" — made with Vittyyo.`
    : `Check out this Vittyyo how-to: "${manual.title}" — ${manual.steps?.length || 0} steps.`;
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  const url = manual.shareUrl || (manual.id ? `${base}/api/share/${manual.id}` : window.location.origin);
  try {
    if (navigator.share) {
      await navigator.share({ title: manual.title, text, url });
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Copied to clipboard");
    }
  } catch (e) {
    if (e && e.name !== "AbortError") toast.error("Couldn't share right now");
  }
}

export function printManual() {
  window.print();
}

// Server-rendered PDF for scripts the browser PDF font can't draw (Chinese, Japanese).
// Streams a translated PDF from the backend (fpdf2 + CJK font) and downloads it.
export async function saveServerPdf(manual, language) {
  toast.loading("Building your full guide…", { id: "pdf" });
  try {
    const res = await api.post(`/manuals/${manual.id}/pdf`, { language }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    const safe = (manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50);
    a.href = url;
    a.download = `stepmystep-${safe}-${language}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("PDF saved", { id: "pdf" });
  } catch (e) {
    toast.error("Couldn't build the PDF — please try again", { id: "pdf" });
  }
}

async function fetchImageDataUrl(id) {
  try {
    const res = await fetch(imageUrl(id));
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function savePdf(manualArg, language = null) {
  let manual = manualArg;
  toast.loading("Building your full guide…", { id: "pdf" });
  // Ensure every step's in-depth explanation is available so the PDF matches the site.
  // DIY manuals are the creator's OWN words — never call the AI (no cost, no filler).
  let explanations = (manual.steps || []).map((s) => s.explanation || "");
  try {
    if (!manual.diy && manual.id && explanations.some((e) => !String(e).trim())) {
      const { data } = await api.post(`/manuals/${manual.id}/explanations`);
      if (Array.isArray(data?.explanations)) explanations = data.explanations;
    }
  } catch {
    /* non-fatal — fall back to whatever explanations we have */
  }
  // Optional language: translate the reader-facing text (cached per language on the backend).
  const native = (manual.language || "en").toLowerCase();
  const wantLang = language ? String(language).toLowerCase() : null;
  if (wantLang && wantLang !== native && manual.id) {
    try {
      const { data } = await api.post(`/manuals/${manual.id}/translate`, { language: wantLang });
      const t = data?.translated;
      if (t) {
        manual = {
          ...manual,
          title: t.title || manual.title,
          estimated_time: t.estimated_time || manual.estimated_time,
          difficulty: t.difficulty || manual.difficulty,
          supplies: t.supplies || manual.supplies,
          safety_warnings: t.safety_warnings || manual.safety_warnings,
          recipe: t.recipe ? { ...manual.recipe, ...t.recipe } : manual.recipe,
          steps: (manual.steps || []).map((s, i) => ({ ...s, ...(t.steps?.[i] || {}) })),
        };
        explanations = (t.steps || []).map((s, i) => s.explanation || explanations[i] || "");
      }
    } catch (e) {
      toast.error("Couldn't translate — building it in the original language", { id: "pdf" });
    }
  }
  try {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    const ensure = (needed) => {
      if (y + needed > pageH - margin) {
        doc.addPage();
        y = margin;
      }
    };
    const purple = [124, 58, 237];
    const ink = [23, 20, 38];
    const gray = [107, 104, 128];

    // Brand
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...purple);
    doc.text("Vittyyo", margin, y);
    doc.setTextColor(...gray);
    doc.setFont("helvetica", "normal");
    doc.text("  ·  Learn To Do Anything", margin + 78, y);
    y += 22;

    // Hero image
    if (manual.hero_image_id) {
      const data = await fetchImageDataUrl(manual.hero_image_id);
      if (data) {
        const imgH = contentW * 0.5;
        ensure(imgH + 10);
        try { doc.addImage(data, "JPEG", margin, y, contentW, imgH, undefined, "FAST"); } catch {}
        y += imgH + 16;
      }
    }

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...ink);
    const titleLines = doc.splitTextToSize(manual.title, contentW);
    ensure(titleLines.length * 26);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 26 + 4;

    // Meta
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...gray);
    doc.text(`${manual.estimated_time || ""}  ·  ${manual.steps?.length || 0} steps  ·  ${manual.difficulty || ""}`, margin, y);
    y += 24;

    const section = (label) => {
      ensure(30);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...purple);
      doc.text(label.toUpperCase(), margin, y);
      y += 16;
      doc.setDrawColor(230, 228, 240);
      doc.line(margin, y - 8, pageW - margin, y - 8);
    };
    const bullets = (arr) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...ink);
      arr.forEach((item) => {
        const lines = doc.splitTextToSize(`•  ${item}`, contentW - 10);
        ensure(lines.length * 15);
        doc.text(lines, margin + 4, y);
        y += lines.length * 15 + 2;
      });
      y += 8;
    };

    if (manual.recipe && manual.recipe.ingredients?.length) {
      const r = manual.recipe;
      section("Recipe");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(...ink);
      const nm = doc.splitTextToSize(r.name || manual.title, contentW);
      ensure(nm.length * 17);
      doc.text(nm, margin, y);
      y += nm.length * 17 + 2;
      const meta = [r.servings, r.prep_time && `Prep ${r.prep_time}`, r.cook_time && `Cook ${r.cook_time}`].filter(Boolean).join("  ·  ");
      if (meta) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...gray);
        ensure(14);
        doc.text(meta, margin, y);
        y += 16;
      }
      bullets(r.ingredients.map((ing) => `${ing.quantity ? ing.quantity + " " : ""}${ing.item}`));
    }
    if (manual.supplies?.length) { section("Required Supplies"); bullets(manual.supplies); }
    if (manual.safety_warnings?.length) { section("Safety Warnings"); bullets(manual.safety_warnings); }

    section("Steps");
    (manual.steps || []).forEach((st, i) => {
      ensure(40);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(...ink);
      const t = doc.splitTextToSize(`${i + 1}. ${st.title}`, contentW);
      ensure(t.length * 17);
      doc.text(t, margin, y);
      y += t.length * 17 + 3;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(...gray);
      if (st.why) {
        const w = doc.splitTextToSize(`Why: ${st.why}`, contentW - 6);
        ensure(w.length * 14);
        doc.text(w, margin + 6, y);
        y += w.length * 14 + 2;
      }
      if (st.tip) {
        const tp = doc.splitTextToSize(`Tip: ${st.tip}`, contentW - 6);
        ensure(tp.length * 14);
        doc.text(tp, margin + 6, y);
        y += tp.length * 14 + 2;
      }
      // In-depth explanation (same content shown on the site's step page)
      const exLines = cleanExplain(explanations[i]);
      if (exLines.length) {
        y += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...purple);
        ensure(13);
        doc.text("IN DEPTH", margin + 6, y);
        y += 13;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.setTextColor(...ink);
        exLines.forEach((para) => {
          const pl = doc.splitTextToSize(para, contentW - 6);
          ensure(pl.length * 14 + 4);
          doc.text(pl, margin + 6, y);
          y += pl.length * 14 + 5;
        });
      }
      y += 8;
    });

    const safeName = (manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50);
    const langSuffix = wantLang && wantLang !== native ? `-${wantLang}` : "";
    doc.save(`stepmystep-${safeName}${langSuffix}.pdf`);
    toast.success("PDF saved", { id: "pdf" });
  } catch (e) {
    toast.error("Couldn't create the PDF", { id: "pdf" });
  }
}


function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function recipeMeta(recipe) {
  return [
    recipe.servings && `Serves ${recipe.servings}`,
    recipe.prep_time && `Prep ${recipe.prep_time}`,
    recipe.cook_time && `Cook ${recipe.cook_time}`,
  ].filter(Boolean).join("  ·  ");
}

// Print ONLY the recipe (name, times, ingredients) — a clean counter-top card.
export function printRecipe(recipe) {
  if (!recipe || !recipe.ingredients?.length) return;
  const rows = recipe.ingredients
    .map((i) => `<li>${i.quantity ? `<b>${escapeHtml(i.quantity)} </b>` : ""}${escapeHtml(i.item)}</li>`)
    .join("");
  const w = window.open("", "_blank", "width=620,height=820");
  if (!w) { toast.error("Enable pop-ups to print the recipe"); return; }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(recipe.name)}</title><style>
    *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#171426;padding:44px;max-width:620px;margin:0 auto}
    h1{font-size:30px;line-height:1.15;margin:0 0 8px}
    .meta{color:#4f7746;font-weight:bold;font-size:13px;letter-spacing:.02em;margin-bottom:22px}
    h2{font-size:15px;text-transform:uppercase;letter-spacing:.06em;color:#171426;border-bottom:2px solid #5d8a52;padding-bottom:7px;margin:26px 0 10px}
    ul{list-style:none;padding:0;margin:0} li{padding:8px 0;border-bottom:1px solid #eee;font-size:16px}
    .brand{margin-top:34px;font-size:11px;color:#9a96ac;letter-spacing:.04em}
    @media print{body{padding:24px}}
  </style></head><body>
    <h1>${escapeHtml(recipe.name)}</h1>
    ${recipeMeta(recipe) ? `<div class="meta">${escapeHtml(recipeMeta(recipe))}</div>` : ""}
    <h2>Ingredients</h2><ul>${rows}</ul>
    <div class="brand">Vittyyo&trade; — Learn To Do Anything</div>
  </body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 350);
}

// Download ONLY the recipe as a compact PDF.
export function saveRecipePdf(recipe) {
  if (!recipe || !recipe.ingredients?.length) return;
  try {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 48;
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    let y = 66;
    doc.setFont("helvetica", "bold"); doc.setFontSize(24); doc.setTextColor(23, 20, 38);
    const nameLines = doc.splitTextToSize(recipe.name || "Recipe", pw - margin * 2);
    doc.text(nameLines, margin, y); y += nameLines.length * 27 + 4;
    const meta = recipeMeta(recipe);
    if (meta) { doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(109, 40, 217); doc.text(meta, margin, y); y += 22; }
    doc.setDrawColor(124, 58, 237); doc.setLineWidth(1.5); doc.line(margin, y, pw - margin, y); y += 24;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(23, 20, 38); doc.text("Ingredients", margin, y); y += 20;
    doc.setFontSize(12);
    recipe.ingredients.forEach((ing) => {
      if (y > ph - 70) { doc.addPage(); y = 66; }
      const txt = `\u2022  ${ing.quantity ? `${ing.quantity} ` : ""}${ing.item}`;
      const lines = doc.splitTextToSize(txt, pw - margin * 2);
      doc.setFont("helvetica", "normal"); doc.setTextColor(74, 71, 92);
      doc.text(lines, margin, y); y += lines.length * 16 + 5;
    });
    y += 22; if (y > ph - 40) { doc.addPage(); y = 66; }
    doc.setFontSize(9); doc.setTextColor(154, 150, 172);
    doc.text("Vittyyo  ·  Learn To Do Anything", margin, y);
    const safe = (recipe.name || "recipe").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50);
    doc.save(`${safe}-recipe.pdf`);
    toast.success("Recipe PDF saved");
  } catch (e) {
    toast.error("Couldn't create the recipe PDF");
  }
}


// Download a one-page "Amazon" affiliate link sheet for the recommended supplies —
// ready to paste into a YouTube / social description. Links are clickable + shown as plain text.
export async function saveAmazonLinksPdf(manual, settings, tag) {
  toast.loading("Building your Amazon link sheet…", { id: "amzn" });
  try {
    let items = (manual.steps && manual.shopping && manual.shopping.items) || null;
    if (!items) {
      const { data } = await api.post(`/manuals/${manual.id}/shopping`);
      items = data?.items || [];
    }
    if (!items.length) {
      toast.error("No supply suggestions to link yet.", { id: "amzn" });
      return;
    }
    const store = storeForCategory(settings, manual.category || "General").store || "Amazon";
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;
    const purple = [124, 58, 237];
    const ink = [23, 20, 38];
    const gray = [107, 104, 128];
    const link = [37, 99, 235];
    const ensure = (needed) => {
      if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
    };

    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...purple);
    doc.text("Vittyyo", margin, y);
    doc.setTextColor(...gray); doc.setFont("helvetica", "normal");
    doc.text("  ·  Recommended supplies", margin + 78, y);
    y += 26;

    doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...ink);
    const titleLines = doc.splitTextToSize(`Supplies for: ${manual.title}`, contentW);
    titleLines.forEach((ln) => { ensure(24); doc.text(ln, margin, y); y += 24; });
    y += 4;

    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...gray);
    const note = doc.splitTextToSize(
      "As an Amazon Associate we may earn from qualifying purchases. Copy any link below into your video description. Suggestions only — always do your own research.",
      contentW
    );
    note.forEach((ln) => { ensure(14); doc.text(ln, margin, y); y += 14; });
    y += 12;

    items.forEach((it, i) => {
      const url = it.url || buildItemUrl(store, it.search_query || it.product_name, tag);
      ensure(60);
      doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...ink);
      const nameLines = doc.splitTextToSize(`${i + 1}. ${it.product_name || it.supply || "Item"}`, contentW);
      nameLines.forEach((ln) => { ensure(16); doc.text(ln, margin, y); y += 16; });
      doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...link);
      const urlLines = doc.splitTextToSize(url, contentW);
      urlLines.forEach((ln, li) => {
        ensure(13);
        if (li === 0) doc.textWithLink(ln, margin, y, { url });
        else doc.text(ln, margin, y);
        y += 13;
      });
      y += 12;
    });

    ensure(20);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(154, 150, 172);
    doc.text("Vittyyo  ·  www.vittyyo.com", margin, pageH - margin + 20);

    const safe = (manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50);
    doc.save(`${safe}-amazon-links.pdf`);
    toast.success("Amazon link sheet saved", { id: "amzn" });
  } catch (e) {
    toast.error("Couldn't build the Amazon link sheet", { id: "amzn" });
  }
}
